import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { favoritesApi } from '../services/apiService';

export type FavoriteType = 'ebook' | 'gallery' | 'audio';

export interface FavoriteItem {
  id: string;
  type: FavoriteType;
  title: string;
  thumbnail?: string;
  subtitle?: string; // For audio: speaker name
  addedAt: string;
}

interface FavoritesContextType {
  favorites: FavoriteItem[];
  isFavorited: (id: string, type: FavoriteType) => boolean;
  addFavorite: (item: Omit<FavoriteItem, 'addedAt'>) => void;
  removeFavorite: (id: string, type: FavoriteType) => void;
  toggleFavorite: (item: Omit<FavoriteItem, 'addedAt'>) => void;
  getFavoritesByType: (type: FavoriteType) => FavoriteItem[];
  clearAllFavorites: () => void;
  isLoading: boolean;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

const STORAGE_KEY = 'ram_naam_favorites';

interface FavoritesProviderProps {
  children: ReactNode;
  userId?: string;
  isLoggedIn: boolean;
}

export const FavoritesProvider: React.FC<FavoritesProviderProps> = ({ 
  children, 
  userId,
  isLoggedIn 
}) => {
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [syncState, setSyncState] = useState<'idle' | 'syncing' | 'synced' | 'error'>('idle');
  
  // Helper functions for sync state (internal, doesn't need context)
  const setSyncing = useCallback(() => setSyncState('syncing'), []);
  const setSynced = useCallback(() => setSyncState('synced'), []);
  const setError = useCallback(() => setSyncState('error'), []);

  // Get localStorage key based on user (fallback)
  const getStorageKey = useCallback(() => {
    return userId ? `${STORAGE_KEY}_${userId}` : STORAGE_KEY;
  }, [userId]);

  // Load favorites from API (with localStorage fallback)
  useEffect(() => {
    if (!isLoggedIn) {
      setFavorites([]);
      return;
    }

    const loadFavorites = async () => {
      setIsLoading(true);
      setSyncing();
      
      try {
        // Try API first
        const apiFavorites = await favoritesApi.getAll();
        if (apiFavorites.length > 0) {
          setFavorites(apiFavorites as FavoriteItem[]);
          // Sync to localStorage as backup
          localStorage.setItem(getStorageKey(), JSON.stringify(apiFavorites));
          setSynced();
        } else {
          // Fallback to localStorage (for migration)
          const stored = localStorage.getItem(getStorageKey());
          if (stored) {
            const localFavorites = JSON.parse(stored);
            setFavorites(localFavorites);
            // Migrate localStorage data to API
            setSyncing();
            for (const fav of localFavorites) {
              await favoritesApi.add({
                id: fav.id,
                type: fav.type,
                title: fav.title,
                thumbnail: fav.thumbnail,
                subtitle: fav.subtitle
              });
            }
            setSynced();
          } else {
            setSynced();
          }
        }
      } catch (error) {
        console.error('Failed to load favorites:', error);
        setError();
        // Fallback to localStorage on API error
        const stored = localStorage.getItem(getStorageKey());
        if (stored) {
          try {
            setFavorites(JSON.parse(stored));
          } catch (e) {
            setFavorites([]);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadFavorites();
  }, [isLoggedIn, getStorageKey, setSyncing, setSynced, setError]);

  // Save favorites (API + localStorage backup)
  const saveFavorites = useCallback((newFavorites: FavoriteItem[]) => {
    if (!isLoggedIn) return;
    setFavorites(newFavorites);
    localStorage.setItem(getStorageKey(), JSON.stringify(newFavorites));
  }, [isLoggedIn, getStorageKey]);

  // Check if an item is favorited
  const isFavorited = useCallback((id: string, type: FavoriteType) => {
    return favorites.some(f => f.id === id && f.type === type);
  }, [favorites]);

  // Add a favorite
  const addFavorite = useCallback(async (item: Omit<FavoriteItem, 'addedAt'>) => {
    if (!isLoggedIn) return;
    
    const newFavorite: FavoriteItem = {
      ...item,
      addedAt: new Date().toISOString()
    };
    
    // Optimistic update
    saveFavorites([...favorites, newFavorite]);
    
    // Sync to API
    try {
      setSyncing();
      await favoritesApi.add(item);
      setSynced();
    } catch (error) {
      console.error('Failed to sync favorite to API:', error);
      setError();
    }
  }, [favorites, saveFavorites, isLoggedIn, setSyncing, setSynced, setError]);

  // Remove a favorite
  const removeFavorite = useCallback(async (id: string, type: FavoriteType) => {
    if (!isLoggedIn) return;
    
    // Optimistic update
    saveFavorites(favorites.filter(f => !(f.id === id && f.type === type)));
    
    // Sync to API
    try {
      setSyncing();
      await favoritesApi.remove(type, id);
      setSynced();
    } catch (error) {
      console.error('Failed to remove favorite from API:', error);
      setError();
    }
  }, [favorites, saveFavorites, isLoggedIn, setSyncing, setSynced, setError]);

  // Toggle favorite
  const toggleFavorite = useCallback((item: Omit<FavoriteItem, 'addedAt'>) => {
    if (!isLoggedIn) return;
    if (isFavorited(item.id, item.type)) {
      removeFavorite(item.id, item.type);
    } else {
      addFavorite(item);
    }
  }, [isFavorited, addFavorite, removeFavorite, isLoggedIn]);

  // Get favorites by type
  const getFavoritesByType = useCallback((type: FavoriteType) => {
    return favorites.filter(f => f.type === type);
  }, [favorites]);

  // Clear all favorites
  const clearAllFavorites = useCallback(async () => {
    if (!isLoggedIn) return;
    
    // Clear locally
    saveFavorites([]);
    
    // Clear from API (remove each one)
    for (const fav of favorites) {
      try {
        await favoritesApi.remove(fav.type, fav.id);
      } catch (error) {
        console.error('Failed to remove favorite:', error);
      }
    }
  }, [saveFavorites, isLoggedIn, favorites]);

  return (
    <FavoritesContext.Provider value={{
      favorites,
      isFavorited,
      addFavorite,
      removeFavorite,
      toggleFavorite,
      getFavoritesByType,
      clearAllFavorites,
      isLoading
    }}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

export default FavoritesContext;
